import React, { ReactNode, useState } from 'react';
import styled from 'astroturf/react';
import { format, formatDistanceToNowStrict } from 'date-fns';

import { useBreakpoint } from 'js/design_system/hooks/useBreakpoint';
import { FollowableUser } from 'js/social/shared/types';
import { ImpressionPropsGenerator } from 'js/social/ImpressionHelper';
import { ImpressionWatcher } from 'js/utils/impressions/ImpressionWatcher';
import { ImpressionEntity } from 'js/utils/impressions/watchImpressions';
import { Body } from 'js/design_system/Body';
import { BodyLink } from 'js/design_system/link/BodyLink';
import AcademiaProfileIcon from '../Icons/academia-profile.svg';
import { Popover } from 'js/design_system/Popover';
import { Modal } from 'js/design_system/Modal';

const AuthorWrapper = styled.div<{
  size: 'xs' | 'sm' | 'md' | 'lg';
  variant?: 'default' | 'dense';
}>`
  @import 'stylesheets/design_system/_breakpoint';

  display: flex;
  align-items: flex-start;
  overflow: hidden;

  @include breakpoint(small) {
    gap: 8px;
  }

  @include breakpoint(medium, large) {
    &.size-xs,
    &.size-sm {
      gap: 8px;
    }

    &.size-md,
    &.size-lg {
      gap: 12px;
    }

    &.variant-dense {
      align-items: center;
    }
  }
`;

const MultiAuthorWrapper = styled.div<{
  size: 'xs' | 'sm' | 'md' | 'lg';
  variant?: 'default' | 'dense';
  single?: boolean;
}>`
  @import 'stylesheets/design_system/_breakpoint';

  display: flex;
  align-items: flex-start;
  overflow: hidden;
  flex-direction: column;
  gap: 8px;

  &.single {
    flex-direction: row;
  }

  @include breakpoint(medium, large) {
    // The header breaks into two lines at small sizes, but sits on one line
    // otherwise
    flex-direction: row;

    &.size-md,
    &.size-lg {
      gap: 12px;
    }

    &.variant-dense {
      align-items: center;
    }
  }
`;

const AuthorInfo = styled.div`
  align-self: center;
  display: flex;
  flex-direction: column;
  overflow: hidden;
`;

const PostName = styled(Body)<{ variant?: 'default' | 'dense' }>`
  @import 'stylesheets/design_system/_util';
  @include text-ellipsis(1);

  &.variant-dense {
    font-weight: 600;
  }
`;

const NormalBody = styled(Body)``;

const DenseBody = styled(Body)`
  line-height: 20px;
  font-weight: 600;
`;

const PostNameLink = styled(BodyLink)`
  @import 'stylesheets/design_system/_util';
  @include text-ellipsis(1);
`;

const Subtitle = styled(Body)`
  color: var(--text-secondary, #535366);
  @import 'stylesheets/design_system/_util';
  @include text-ellipsis(1);
`;

const AvatarWrapper = styled.div`
  flex-shrink: 0;

  & > svg {
    display: block;
  }
`;

const Avatar = styled.img`
  border-radius: 50%;
  display: block;
`;

const AvatarLink = styled.a`
  display: block;
  line-height: 0;
  flex-shrink: 0;
`;

const OverflowImpressionWatcher = styled(ImpressionWatcher)`
  display: flex;
  overflow: hidden;
`;

const Timestamp = styled(Body)`
  color: var(--text-secondary, #535366);
`;

type UserImageProps = {
  kind: 'user';
  user: FollowableUser;
};

type RecommendationImageProps = {
  kind: 'recommendation';
};

type SharedImageProps = {
  // These coorespond to the following non-mobile avatar sizes:
  //     32     40     48     56
  size: 'xs' | 'sm' | 'md' | 'lg';
};

type ImageProps = SharedImageProps &
  (UserImageProps | RecommendationImageProps);

export const AuthorAvatar = ({ size, ...restProps }: ImageProps) => {
  const breakpoint = useBreakpoint();
  let avatarSize;
  switch (size) {
    case 'xs':
      avatarSize = breakpoint === 'small' ? 24 : 32;
      break;
    case 'sm':
      avatarSize = breakpoint === 'small' ? 32 : 40;
      break;
    case 'md':
      avatarSize = breakpoint === 'small' ? 40 : 48;
      break;
    case 'lg':
      // Note: no examples of lg + small breakpoint exist, 48px is a guess
      avatarSize = breakpoint === 'small' ? 48 : 56;
      break;
  }

  if (restProps.kind === 'user') {
    return (
      <AvatarLink href={restProps.user.url}>
        <Avatar
          style={{ width: avatarSize, height: avatarSize }}
          src={restProps.user.photo}
        />
      </AvatarLink>
    );
  } else if (restProps.kind === 'recommendation') {
    return (
      <AvatarWrapper>
        <AcademiaProfileIcon size={avatarSize} />
      </AvatarWrapper>
    );
  } else {
    return null;
  }
};

const MultiUserAvatarContainer = styled.div`
  display: flex;
  flex-direction: row;
`;

const MultiUserAvatarWrapper = styled.div`
  position: relative;

  &:not(:first-child) {
    margin-left: -9px;
  }
`;

const MultiUserAvatarClip = styled.div`
  clip-path: circle(48% at 50% 50%);
`;

const MultiUserAvatarBorder = styled.div`
  border-radius: 50%;
  border: 2px solid white;
  position: absolute;
  inset: 0px;
`;

const MultiUserAvatarCount = styled(Body)`
  color: #fff;
  background-color: rgba(0, 0, 0, 0.59);
  clip-path: circle(48% at 50% 50%);
  position: absolute;
  inset: 0.5px;
  display: flex;
  align-items: center;
  justify-content: center;
`;

const MultiUserAvatar = ({
  users,
  size,
}: SharedImageProps & { users: FollowableUser[] }) => {
  if (users.length === 1) {
    return <AuthorAvatar kind="user" user={users[0]} size={size} />;
  }

  const extraCount = users.length - 3;

  return (
    <>
      <MultiUserAvatarContainer>
        {users.slice(0, 3).map((user, i) => (
          <MultiUserAvatarWrapper key={user.id}>
            <MultiUserAvatarClip>
              <AuthorAvatar kind="user" user={user} size={size} />
            </MultiUserAvatarClip>
            {i === 2 && extraCount > 0 && (
              <MultiUserAvatarCount bold size="md">
                +{extraCount}
              </MultiUserAvatarCount>
            )}
            <MultiUserAvatarBorder />
          </MultiUserAvatarWrapper>
        ))}
      </MultiUserAvatarContainer>
    </>
  );
};

const MultiUserPopoverScrollContainer = styled.div`
  overflow-y: auto;
  padding: var(--spacing-xs-12, 12px) var(--spacing-xs-16, 16px);
  min-width: 400px;
`;

const MultiUserPopover = styled.div`
  display: flex;
  overflow: hidden;
  margin-left: -16px;
  border-radius: var(--radius-corner-radius-default, 16px);
  border: 1px solid var(--stroke-dark, #141413);
  background: var(--background-white, #fff);
`;

const MultiUserTrigger = styled.div`
  cursor: pointer;
`;

const MultiUserTitle = ({
  users,
  userTitle,
  groupName,
  action,
  paperType,
  size,
  className,
  variant = 'default',
}: MultiUserProps &
  SharedImageProps & { className?: string; variant?: 'default' | 'dense' }) => {
  const groupNameText = groupName ? ` in ${groupName}` : '';
  const paperNameText = paperType === 'paper' ? 'a paper' : 'a trending paper';
  const verbText = action === 'save' ? 'saved' : 'viewed';
  const fontSize = size === 'xs' || variant === 'dense' ? 'md' : 'lg';
  const BodyComponent = variant === 'dense' ? DenseBody : NormalBody;

  const renderUserTitle = (displayName: string, title: string) => (
    <BodyComponent
      size={fontSize}
      className={className}
      bold={variant === 'dense'}
    >
      {variant === 'default' ? (
        <>
          <Body as="strong" size={fontSize} bold>
            {displayName}, {title}
          </Body>
          {groupNameText}, {verbText} {paperNameText}
        </>
      ) : (
        <>
          {displayName}, {title}
          {groupNameText}, {verbText} {paperNameText}
        </>
      )}
    </BodyComponent>
  );

  const renderMultiUserTitle = (count: number, title: string) => (
    <BodyComponent
      size={fontSize}
      className={className}
      bold={variant === 'dense'}
    >
      {variant === 'default' ? (
        <>
          <Body as="strong" size={fontSize} bold>
            {count} {title}
          </Body>
          {groupNameText} {verbText} {paperNameText}
        </>
      ) : (
        <>
          {count} {title}
          {groupNameText} {verbText} {paperNameText}
        </>
      )}
    </BodyComponent>
  );

  if (
    !userTitle ||
    (userTitle !== 'published' && userTitle !== 'highly-cited')
  ) {
    return null;
  }

  const title =
    userTitle === 'published'
      ? 'published researcher'
      : 'highly-cited researcher';
  const titles = `${title}s`;

  return users.length === 1
    ? renderUserTitle(users[0].displayName, title)
    : renderMultiUserTitle(users.length, titles);
};

const MultiAuthorWrapperWithInteraction = ({
  avatarInteractionContents,
  children,
}: {
  avatarInteractionContents?: ReactNode;
  children: ReactNode;
}) => {
  const [showInteractionModal, setShowInteractionModal] = useState(false);
  const breakpoint = useBreakpoint();

  if (!avatarInteractionContents) {
    return <>{children}</>;
  }

  if (breakpoint === 'small') {
    return (
      <>
        <MultiUserTrigger onClick={() => setShowInteractionModal(true)}>
          {children}
        </MultiUserTrigger>
        <Modal
          open={showInteractionModal}
          onClose={() => setShowInteractionModal(false)}
        >
          {avatarInteractionContents}
        </Modal>
      </>
    );
  } else {
    return (
      <Popover
        render={() => (
          <MultiUserPopover>
            <MultiUserPopoverScrollContainer>
              {avatarInteractionContents}
            </MultiUserPopoverScrollContainer>
          </MultiUserPopover>
        )}
        maxWidth={480}
        maxHeight={200}
        placement="top-start"
        offset={17}
      >
        <MultiUserTrigger>{children}</MultiUserTrigger>
      </Popover>
    );
  }
};

type UserProps = {
  kind: 'user';
  user: FollowableUser;
  link?: boolean;
  timestamp?: string;
  showTimestamp: boolean;
  extraSubtitles?: string[];
};

type MultiUserProps = {
  kind: 'multi-user';
  users: FollowableUser[];
  userTitle: 'published' | 'highly-cited';
  groupName?: string;
  action: 'save' | 'view';
  paperType: 'paper' | 'trending';
  avatarInteractionContents?: ReactNode;
};

type RecommendationProps = {
  kind: 'recommendation';
  recommendation: {
    name: string;
    subtitle?: string;
  };
};

type SharedProps = {
  impressionPropsGen: ImpressionPropsGenerator;
  showAvatar: boolean;
  className?: string;
} & SharedImageProps;

type Props = SharedProps &
  (UserProps | MultiUserProps | RecommendationProps) & {
    variant?: 'default' | 'dense';
  };

// This component should be used to render any instance of a "post author", such
// as in the header of a news feed unit or a comment author. It consists of a
// name, some optional subtitles, and an optional avatar.
//  ________
// /        \   Title
// | avatar |   Subtitle
// \        /   (timestamp)
//  --------
//
// The 'kind' prop determines what kind of data should be used to generate the
// header. It can be a 'user' for a human author, 'recommendation' for an
// academia-generated piece of content, or 'multi-user' for posts which
// reference multiple authors.
//
// The 'size' prop determines how large the avatar and fonts should be. See
// `SharedImageProps` above to determine which size to use.
//
// If rendering the avatar and text separately, pass `showAvatar={false}` and
// render the avatar with the same size using `AuthorAvatar`.
export const AuthorHeader = ({
  impressionPropsGen,
  showAvatar,
  size,
  className,
  variant = 'default',
  ...restProps
}: Props) => {
  if (restProps.kind === 'user') {
    const {
      kind,
      user,
      link = true,
      timestamp,
      showTimestamp,
      extraSubtitles,
    } = restProps;

    const body = (
      <AuthorInfo className={showAvatar ? undefined : className}>
        <OverflowImpressionWatcher
          {...impressionPropsGen(ImpressionEntity.User, user.id)('prof-n')()}
        >
          {link ? (
            <PostNameLink
              size={size === 'xs' ? 'sm' : 'md'}
              bold
              href={user.url}
            >
              {user.displayName}
            </PostNameLink>
          ) : (
            <PostName size={size === 'xs' ? 'sm' : 'md'} bold>
              {user.displayName}
            </PostName>
          )}
        </OverflowImpressionWatcher>
        {user.university && (
          <Subtitle size={size === 'xs' ? 'xs' : 'sm'}>
            {[
              user.position,
              user.university?.displayName,
              user.department?.displayName,
            ]
              .filter(Boolean)
              .join(', ')}
          </Subtitle>
        )}
        {showTimestamp && timestamp && (
          <time dateTime={timestamp} title={format(new Date(timestamp), 'PPP')}>
            <Timestamp size={size === 'xs' ? 'xs' : 'sm'}>
              {formatDistanceToNowStrict(new Date(timestamp))}
            </Timestamp>
          </time>
        )}
        {extraSubtitles &&
          extraSubtitles.map((subtitle, i) => (
            <Subtitle key={i} size={size === 'xs' ? 'xs' : 'sm'}>
              {subtitle}
            </Subtitle>
          ))}
      </AuthorInfo>
    );

    if (showAvatar) {
      return (
        <AuthorWrapper size={size} className={className} variant={variant}>
          <AuthorAvatar kind={kind} user={user} size={size} />
          {body}
        </AuthorWrapper>
      );
    } else {
      return body;
    }
  } else if (restProps.kind === 'multi-user') {
    const {
      kind,
      users,
      userTitle,
      groupName,
      action,
      paperType,
      avatarInteractionContents,
    } = restProps;

    const body = (
      <MultiUserTitle
        className={showAvatar ? undefined : className}
        kind={kind}
        users={users}
        userTitle={userTitle}
        groupName={groupName}
        action={action}
        paperType={paperType}
        size={size}
        variant={variant}
      />
    );

    if (showAvatar) {
      return (
        <MultiAuthorWrapper
          size={size}
          className={className}
          variant={variant}
          single={users.length === 1}
        >
          <MultiAuthorWrapperWithInteraction
            avatarInteractionContents={avatarInteractionContents}
          >
            <MultiUserAvatar users={users} size={size} />
          </MultiAuthorWrapperWithInteraction>
          {body}
        </MultiAuthorWrapper>
      );
    } else {
      return (
        <MultiAuthorWrapperWithInteraction
          avatarInteractionContents={avatarInteractionContents}
        >
          {body}
        </MultiAuthorWrapperWithInteraction>
      );
    }
  } else if (restProps.kind === 'recommendation') {
    const { kind, recommendation } = restProps;
    const body = (
      <AuthorInfo className={showAvatar ? undefined : className}>
        <PostName size={size === 'xs' ? 'sm' : 'md'} bold variant={variant}>
          {recommendation.name}
        </PostName>
        {recommendation.subtitle && (
          <Subtitle size={size === 'xs' ? 'xs' : 'sm'}>
            {recommendation.subtitle}
          </Subtitle>
        )}
      </AuthorInfo>
    );

    if (showAvatar) {
      return (
        <AuthorWrapper size={size} className={className} variant={variant}>
          <AuthorAvatar kind={kind} size={size} />
          {body}
        </AuthorWrapper>
      );
    } else {
      return body;
    }
  } else {
    return null;
  }
};
